import {
  DIAGRAM_MAX_ELEMENTS,
  elementsOutline,
  normalizeBlockElements,
  rescaleElementsToPitch,
  roundCoordinate,
  shiftElements,
  type DiagramElement,
  type DiagramPitch,
} from '@ukinfo/shared';
import { formatMeters, measureMeters, type FieldPoint } from './diagramEdit';
import { renumbered } from './diagramGroup';

/**
 * Bausteine in der Skizze: eine Auswahl als Baustein speichern und einen Aufbau oder Baustein einfuegen -
 * reine Rechnung ohne React.
 *
 * Ein Baustein liegt relativ zu seinem Umriss (linke obere Ecke = 0, 0) und kennt das Feld (`pitch`), auf dem er
 * entstanden ist. Beim Einfuegen wird er auf das Feld der Skizze umgerechnet (Hoehe in Zeichnungseinheiten, die Form
 * bleibt), um die Mitte des Umrisses auf den Zielpunkt gesetzt und im Feld gehalten. Spieler bekommen fortlaufende
 * Nummern je Mannschaft, wie beim Duplizieren.
 */

export interface BlockContent {
  /** Feld, auf dem die Elemente entstanden sind. */
  pitch: DiagramPitch;
  elements: DiagramElement[];
}

/** Der Baustein aus den markierten Elementen: relativ zum Umriss abgelegt (die Eingabe bleibt unveraendert). */
export function blockFromSelection(elements: readonly DiagramElement[], indices: readonly number[], pitch: DiagramPitch): BlockContent {
  const picked = [...new Set(indices)].sort((a, b) => a - b).flatMap((index) => (elements[index] ? [elements[index]!] : []));
  return { pitch, elements: normalizeBlockElements(picked) };
}

export type PlaceResult = { ok: true; elements: DiagramElement[] } | { ok: false; problem: string };

export interface PlaceOptions {
  /** Feld der Skizze, in die eingefuegt wird. */
  pitch: DiagramPitch;
  /** Elemente, die die Skizze schon hat (fuer die Nummerierung und die Grenze von 80). */
  existing: readonly DiagramElement[];
  /** Mitte, auf die der Umriss des Bausteins kommt (Feldprozent). */
  center: FieldPoint;
  /**
   * Breite des Felds in Metern, wenn die Beschriftung der Masslinien aus ihrer Laenge neu gerechnet werden soll
   * (fertige Aufbauten: sie sind fuer die freie Flaeche gezeichnet, auf einem anderen Feld waere dieselbe Strecke laenger).
   */
  relabelWidthMeters?: number;
}

/**
 * Setzt einen Baustein auf das Feld der Skizze: die neuen Elemente (umgerechnet, zentriert, nummeriert) oder den Grund,
 * warum es nicht geht (zu gross fuers Feld, mehr als 80 Elemente).
 */
export function placeBlock(block: BlockContent, options: PlaceOptions): PlaceResult {
  const { pitch, existing, center } = options;
  if (block.elements.length === 0) return { ok: false, problem: 'Der Baustein ist leer.' };
  if (existing.length + block.elements.length > DIAGRAM_MAX_ELEMENTS) {
    return {
      ok: false,
      problem: `Der Baustein hat ${block.elements.length} Elemente – bei ${existing.length} Elementen wären das mehr als ${DIAGRAM_MAX_ELEMENTS}. Lösche ein paar Elemente.`,
    };
  }

  const rescaled = rescaleElementsToPitch(block.elements, block.pitch, pitch);
  const box = elementsOutline(rescaled)!;
  const width = box.maxX - box.minX;
  const height = box.maxY - box.minY;
  if (width > 100 || height > 100) return { ok: false, problem: 'Der Baustein ist zu groß für dieses Feld.' };

  // Umriss auf den Zielpunkt zentrieren und im Feld halten (die Gruppe bleibt dabei zusammen).
  const left = Math.min(100 - width, Math.max(0, roundCoordinate(center.x - width / 2)));
  const top = Math.min(100 - height, Math.max(0, roundCoordinate(center.y - height / 2)));
  const placed = shiftElements(rescaled, left - box.minX, top - box.minY);

  const result: DiagramElement[] = [];
  for (const element of placed) {
    let next = renumbered(element, [...existing, ...result]);
    if (next.type === 'measure' && next.label !== undefined && options.relabelWidthMeters) {
      const meters = measureMeters({ x: next.from[0], y: next.from[1] }, { x: next.to[0], y: next.to[1] }, pitch, options.relabelWidthMeters);
      next = { ...next, label: formatMeters(meters) };
    }
    result.push(next);
  }
  return { ok: true, elements: result };
}
